import { OmnisearchSelect } from '@/components/ui/omnisearch-select';
import { legalDocumentsApi } from '@/lib/pillarApi';
import type { LegalDocumentListItem } from '@pillar-portal/api-types';
import { LegalDocumentPurpose, Status } from '@pillar-portal/database/dist/frontend';

export interface PlatformDefaultReplacement {
    readonly legalDocumentUuid: string;
    readonly label: string;
}

interface PlatformDefaultReplacementSelectProps {
    readonly replacement: PlatformDefaultReplacement | null;
    readonly onChange: (replacement: PlatformDefaultReplacement | null) => void;
}

function documentLabel(legalDocument: LegalDocumentListItem): string {
    return `${legalDocument.legal_document_title} (${legalDocument.legal_document_version})`;
}

/** Picks the active user agreement that becomes the platform default in the same step. */
export function PlatformDefaultReplacementSelect({
    replacement,
    onChange,
}: PlatformDefaultReplacementSelectProps) {
    return (
        <div className="space-y-2">
            <p>
                This is the last platform default. Choose the user agreement that replaces it as the
                platform default from now.
            </p>
            <OmnisearchSelect<LegalDocumentListItem, string>
                inputId="legal-platform-default-replacement"
                label="Replacement platform default"
                placeholder="Search active user agreements..."
                pageSize={25}
                selectedItemId={replacement?.legalDocumentUuid ?? null}
                selectedItemLabel={replacement?.label}
                onClearSelection={() => onChange(null)}
                fetchPage={async (params) => {
                    const response = await legalDocumentsApi.searchLegalDocumentsPage({
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                    });
                    return {
                        items: response.legal_documents.filter(
                            (legalDocument) =>
                                legalDocument.status === Status.ACTIVE &&
                                legalDocument.legal_document_purpose ===
                                    LegalDocumentPurpose.USER_AGREEMENT
                        ),
                        pagination: response.pagination,
                    };
                }}
                getItemId={(item) => item.legal_document_uuid}
                getItemLabel={documentLabel}
                getItemFilterText={documentLabel}
                onSelect={(item) =>
                    onChange({
                        legalDocumentUuid: item.legal_document_uuid,
                        label: documentLabel(item),
                    })
                }
            />
        </div>
    );
}
